import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Edit2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { supabase } from '../supabaseClient';

const PrinterUsage = () => {
  const navigate = useNavigate();
  
  // State for current user
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  
  // State for loading
  const [isLoading, setIsLoading] = useState(true);
  
  // State for editing usage logs (admin only)
  const [editingUsageLog, setEditingUsageLog] = useState(null);
  const [editingUsageField, setEditingUsageField] = useState(null);
  const [editingUsageValue, setEditingUsageValue] = useState('');
  
  // State for spools
  const [spools, setSpools] = useState([]);

  // State for usage logs
  const [usageLogs, setUsageLogs] = useState([]);

  // State for modals
  const [showSpoolInventoryModal, setShowSpoolInventoryModal] = useState(false);
  const [showAddSpoolModal, setShowAddSpoolModal] = useState(false);
  const [showAddUsageModal, setShowAddUsageModal] = useState(false);
  const [editingSpoolId, setEditingSpoolId] = useState(null);
  const [editingField, setEditingField] = useState(null); // Track which field is being edited: 'color', 'material', 'remainingWeight'
  
  // State for delete confirmation
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, type: null, id: null, data: null });

  // State for new spool form
  const [newSpool, setNewSpool] = useState({
    initialWeight: 1000,
    color: '',
    material: 'PLA'
  });

  // State for new usage form
  const [newUsage, setNewUsage] = useState({
    purpose: '',
    spoolUsages: [{ spoolId: '', amountUsed: '' }]
  });

  // Calculate statistics
  const totalSpools = spools.length;
  const totalInitialWeight = spools.reduce((sum, spool) => sum + spool.initialWeight, 0);
  const totalRemainingWeight = spools.reduce((sum, spool) => sum + spool.remainingWeight, 0);
  const spoolsWithMaterial = spools.filter(s => s.remainingWeight > 0).length;

  // Get current user and role from Supabase
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUser(user);
        
        // Load user role
        const { data: userData, error } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single();
        
        if (!error && userData) {
          setUserRole(userData.role);
        }
      }
    };
    getCurrentUser();
  }, []);

  // Load data and setup real-time subscriptions
  useEffect(() => {
    if (!currentUser) return;

    // Load data in parallel for faster initial load
    const loadData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([loadSpools(), loadUsageLogs()]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();

    // Setup real-time subscription for instant updates
    const channel = supabase
      .channel('printer_usage_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'printer_usage_logs'
        },
        (payload) => {
          // Reload data on any change (insert, update, delete)
          if (payload.eventType === 'INSERT' || payload.eventType === 'DELETE') {
            // For inserts and deletes, reload both to ensure consistency
            loadSpools();
            loadUsageLogs();
          } else if (payload.eventType === 'UPDATE') {
            // For updates, check what was updated
            const updated = payload.new;
            if (updated.record_type === 'spool') {
              loadSpools();
            } else if (updated.record_type === 'usage') {
              loadUsageLogs();
            }
          }
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser]);

  // Load spools from database
  const loadSpools = async () => {
    try {
      const { data, error } = await supabase
        .from('printer_usage_logs')
        .select('id, spool_id, spool_color, spool_material, initial_weight, remaining_weight')
        .eq('record_type', 'spool')
        .is('deleted_at', null)
        .order('spool_id', { ascending: true });

      if (error) throw error;

      const spoolsList = data.map(record => ({
        id: record.spool_id,
        initialWeight: Number(record.initial_weight),
        remainingWeight: Number(record.remaining_weight),
        color: record.spool_color,
        material: record.spool_material,
        dbId: record.id
      }));

      setSpools(spoolsList);
    } catch (error) {
      console.error('Error loading spools:', error);
      toast.error('Failed to load spools');
    }
  };

  // Load usage logs from database
  const loadUsageLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('printer_usage_logs')
        .select('id, user_id, user_name, user_email, usage_date_time, purpose, spools_used')
        .eq('record_type', 'usage')
        .is('deleted_at', null)
        .order('usage_date_time', { ascending: false });

      if (error) throw error;

      const logsList = data.map(record => ({
        id: record.id,
        dateTime: new Date(record.usage_date_time),
        spools: record.spools_used,
        purpose: record.purpose,
        user: record.user_name,
        createdBy: record.user_email,
        userId: record.user_id  // Store user_id for proper auth validation
      }));

      setUsageLogs(logsList);
    } catch (error) {
      console.error('Error loading usage logs:', error);
      toast.error('Failed to load usage logs');
    }
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (!currentUser) return 'Unknown User';
    return currentUser.user_metadata?.name || 
           currentUser.user_metadata?.full_name || 
           currentUser.email?.split('@')[0] || 
           'User';
  };

  // Handle double-click to edit usage log field (admin only)
  const handleUsageLogDoubleClick = (log, spoolUsage, field, spoolIndex) => {
    if (userRole !== 'admin') return;
    
    setEditingUsageLog({ logId: log.id, spoolId: spoolUsage.spoolId, spoolIndex });
    setEditingUsageField(field);
    
    if (field === 'purpose') {
      setEditingUsageValue(log.purpose);
    } else if (field === 'amountUsed') {
      setEditingUsageValue(spoolUsage.amountUsed.toString());
    } else if (field === 'dateTime') {
      setEditingUsageValue(log.dateTime.toISOString().slice(0, 16));
    }
  };

  // Handle save edited usage log
  const handleSaveUsageLogEdit = async () => {
    if (!editingUsageLog || !editingUsageField) return;

    try {
      // Get the current log
      const { data: currentLogData, error: fetchError } = await supabase
        .from('printer_usage_logs')
        .select('*')
        .eq('id', editingUsageLog.logId)
        .single();

      if (fetchError) throw fetchError;

      const currentLog = usageLogs.find(l => l.id === editingUsageLog.logId);
      const spoolUsages = [...currentLogData.spools_used];
      const oldSpoolUsage = spoolUsages[editingUsageLog.spoolIndex];

      if (editingUsageField === 'amountUsed') {
        const newAmount = Number(editingUsageValue);
        if (isNaN(newAmount) || newAmount <= 0) {
          toast.error('Please enter a valid amount');
          return;
        }

        const oldAmount = oldSpoolUsage.amountUsed;
        const difference = newAmount - oldAmount;

        // Check if spool has enough material
        const spool = spools.find(s => s.id === oldSpoolUsage.spoolId);
        if (!spool) {
          toast.error('Spool not found');
          return;
        }

        const currentAvailable = spool.remainingWeight + oldAmount;
        if (newAmount > currentAvailable) {
          toast.error(`Insufficient material. Only ${currentAvailable}g available`);
          return;
        }

        // Update the amount in the spools_used array
        spoolUsages[editingUsageLog.spoolIndex] = {
          ...oldSpoolUsage,
          amountUsed: newAmount
        };

        // Update usage log
        const { error: updateError } = await supabase
          .from('printer_usage_logs')
          .update({ spools_used: spoolUsages })
          .eq('id', editingUsageLog.logId);

        if (updateError) throw updateError;

        // Update spool remaining weight
        const newRemainingWeight = spool.remainingWeight - difference;
        const { error: spoolError } = await supabase
          .from('printer_usage_logs')
          .update({ remaining_weight: newRemainingWeight })
          .eq('spool_id', oldSpoolUsage.spoolId)
          .eq('record_type', 'spool');

        if (spoolError) throw spoolError;

        toast.success('Amount updated and spool inventory adjusted');
      } else if (editingUsageField === 'purpose') {
        if (!editingUsageValue.trim()) {
          toast.error('Purpose cannot be empty');
          return;
        }

        const { error } = await supabase
          .from('printer_usage_logs')
          .update({ purpose: editingUsageValue.trim() })
          .eq('id', editingUsageLog.logId);

        if (error) throw error;
        toast.success('Purpose updated');
      } else if (editingUsageField === 'dateTime') {
        const newDateTime = new Date(editingUsageValue);
        if (isNaN(newDateTime.getTime())) {
          toast.error('Invalid date/time');
          return;
        }

        const { error } = await supabase
          .from('printer_usage_logs')
          .update({ usage_date_time: newDateTime.toISOString() })
          .eq('id', editingUsageLog.logId);

        if (error) throw error;
        toast.success('Date/Time updated');
      }

      // Reload data
      await Promise.all([loadSpools(), loadUsageLogs()]);
      
      // Clear editing state
      setEditingUsageLog(null);
      setEditingUsageField(null);
      setEditingUsageValue('');
    } catch (error) {
      console.error('Error updating usage log:', error);
      toast.error('Failed to update usage log');
    }
  };

  // Handle cancel editing
  const handleCancelUsageLogEdit = () => {
    setEditingUsageLog(null);
    setEditingUsageField(null);
    setEditingUsageValue('');
  };

  // Handle add new spool
  const handleAddSpool = async () => {
    if (!newSpool.color.trim()) {
      toast.error('Please enter spool color');
      return;
    }

    if (!currentUser) {
      toast.error('User not authenticated');
      return;
    }

    try {
      // Get next spool ID
      const { data: existingSpools } = await supabase
        .from('printer_usage_logs')
        .select('spool_id')
        .eq('record_type', 'spool')
        .order('spool_id', { ascending: false })
        .limit(1);

      const newSpoolId = existingSpools && existingSpools.length > 0 
        ? existingSpools[0].spool_id + 1 
        : 1;

      // Insert spool into database
      const { error } = await supabase
        .from('printer_usage_logs')
        .insert({
          record_type: 'spool',
          user_id: currentUser.id,
          user_email: currentUser.email,
          user_name: getUserDisplayName(),
          spool_id: newSpoolId,
          spool_color: newSpool.color.trim(),
          spool_material: newSpool.material,
          initial_weight: Number(newSpool.initialWeight),
          remaining_weight: Number(newSpool.initialWeight)
        });

      if (error) throw error;

      await loadSpools();
      setNewSpool({ initialWeight: 1000, color: '', material: 'PLA' });
      setShowAddSpoolModal(false);
      toast.success('Spool added successfully');
    } catch (error) {
      console.error('Error adding spool:', error);
      toast.error('Failed to add spool');
    }
  };

  // Handle edit spool
  const handleEditSpool = (id, field, value) => {
    setSpools(spools.map(spool => 
      spool.id === id ? { ...spool, [field]: value } : spool
    ));
  };

  // Handle save edited spool
  const handleSaveSpoolEdit = async () => {
    try {
      const spool = spools.find(s => s.id === editingSpoolId);
      if (!spool || !spool.dbId) {
        toast.error('Spool not found');
        return;
      }

      const updateData = {};
      
      // Only update the field that was edited
      if (editingField === 'color') {
        updateData.spool_color = spool.color;
      } else if (editingField === 'material') {
        updateData.spool_material = spool.material;
      } else if (editingField === 'remainingWeight') {
        updateData.remaining_weight = Number(spool.remainingWeight);
      }

      const { error } = await supabase
        .from('printer_usage_logs')
        .update(updateData)
        .eq('id', spool.dbId);

      if (error) throw error;

      setEditingSpoolId(null);
      setEditingField(null);
      toast.success('Spool updated successfully');
    } catch (error) {
      console.error('Error updating spool:', error);
      toast.error('Failed to update spool');
      await loadSpools();
    }
  };

  // Handle delete spool - show confirmation
  const handleDeleteSpool = async (id) => {
    // Check if spool is used in any logs
    const { data: usageLogs, error: checkError } = await supabase
      .from('printer_usage_logs')
      .select('spools_used')
      .eq('record_type', 'usage')
      .is('deleted_at', null);

    if (checkError) {
      toast.error('Failed to check spool usage');
      return;
    }

    const isUsed = usageLogs.some(log => {
      const spools = log.spools_used || [];
      return spools.some(s => s.spoolId === id);
    });

    if (isUsed) {
      toast.error('Cannot delete spool that has usage history');
      return;
    }

    const spool = spools.find(s => s.id === id);
    if (!spool || !spool.dbId) {
      toast.error('Spool not found');
      return;
    }
    
    // Show confirmation dialog
    setDeleteConfirmation({ show: true, type: 'spool', id: spool.dbId, data: spool });
  };
  
  // Confirm delete spool
  const confirmDeleteSpool = async () => {
    const { id: dbId, data: spool } = deleteConfirmation;
    setDeleteConfirmation({ show: false, type: null, id: null, data: null });
    
    try {

      // Hard delete since it's not used
      const { error } = await supabase
        .from('printer_usage_logs')
        .delete()
        .eq('id', dbId)
        .eq('record_type', 'spool');

      if (error) throw error;

      toast.success(`Spool #${spool.id} (${spool.color}) deleted successfully`);
    } catch (error) {
      console.error('Error deleting spool:', error);
      toast.error('Failed to delete spool');
      await loadSpools();
    }
  };

  // Handle add usage
  const handleAddUsage = async () => {
    if (!newUsage.purpose.trim()) {
      toast.error('Please enter purpose');
      return;
    }

    if (!currentUser) {
      toast.error('User not authenticated');
      return;
    }

    const validSpoolUsages = newUsage.spoolUsages.filter(
      su => su.spoolId && su.amountUsed && Number(su.amountUsed) > 0
    );

    if (validSpoolUsages.length === 0) {
      toast.error('Please add at least one spool usage');
      return;
    }

    for (const su of validSpoolUsages) {
      const spool = spools.find(s => s.id === Number(su.spoolId));
      if (!spool) {
        toast.error(`Spool ${su.spoolId} not found`);
        return;
      }
      if (spool.remainingWeight < Number(su.amountUsed)) {
        toast.error(`Spool ${spool.id} (${spool.color}) only has ${spool.remainingWeight}g remaining`);
        return;
      }
    }

    try {
      // Prepare spools_used array with full spool info
      const spoolsUsedData = validSpoolUsages.map(su => {
        const spool = spools.find(s => s.id === Number(su.spoolId));
        return {
          spoolId: Number(su.spoolId),
          spoolColor: spool.color,
          spoolMaterial: spool.material,
          amountUsed: Number(su.amountUsed)
        };
      });

      // Insert usage log
      const { error: insertError } = await supabase
        .from('printer_usage_logs')
        .insert({
          record_type: 'usage',
          user_id: currentUser.id,
          user_email: currentUser.email,
          user_name: getUserDisplayName(),
          usage_date_time: new Date().toISOString(),
          purpose: newUsage.purpose.trim(),
          spools_used: spoolsUsedData
        });

      if (insertError) throw insertError;

      // Update remaining weights for all used spools
      for (const su of validSpoolUsages) {
        const spool = spools.find(s => s.id === Number(su.spoolId));
        if (spool && spool.dbId) {
          const newRemainingWeight = spool.remainingWeight - Number(su.amountUsed);
          
          const { data, error: updateError, count } = await supabase
            .from('printer_usage_logs')
            .update({ remaining_weight: newRemainingWeight })
            .eq('id', spool.dbId)
            .eq('record_type', 'spool')
            .select();

          if (updateError) {
            throw updateError;
          }
        }
      }

      // Real-time subscription will auto-update, but reload for immediate feedback
      await Promise.all([loadSpools(), loadUsageLogs()]);
      setNewUsage({
        purpose: '',
        spoolUsages: [{ spoolId: '', amountUsed: '' }]
      });
      setShowAddUsageModal(false);
      toast.success('Usage recorded successfully');
    } catch (error) {
      console.error('Error adding usage:', error);
      toast.error('Failed to record usage');
    }
  };

  // Handle delete usage log - show confirmation
  const handleDeleteUsage = (logId) => {
    const log = usageLogs.find(l => l.id === logId);
    
    if (!log) {
      toast.error('Usage log not found');
      return;
    }
    
    if (!currentUser) {
      toast.error('User not authenticated');
      return;
    }
    
    // Check ownership using user ID (more secure than email)
    if (currentUser.id !== log.userId) {
      toast.error('You can only delete your own usage logs');
      return;
    }
    
    // Show confirmation dialog
    setDeleteConfirmation({ show: true, type: 'usage', id: logId, data: log });
  };
  
  // Confirm delete usage log
  const confirmDeleteUsage = async () => {
    const { id: logId, data: log } = deleteConfirmation;
    setDeleteConfirmation({ show: false, type: null, id: null, data: null });
    
    try {

      // 1. Restore spool weights first (add back the used amounts)
      const updatePromises = log.spools.map(async (spoolUsage) => {
        const spool = spools.find(s => s.id === spoolUsage.spoolId);
        if (!spool?.dbId) {
          console.warn(`Spool ${spoolUsage.spoolId} not found`);
          return { error: null };
        }

        const restoredWeight = spool.remainingWeight + spoolUsage.amountUsed;
        
        return supabase
          .from('printer_usage_logs')
          .update({ remaining_weight: restoredWeight })
          .eq('id', spool.dbId)
          .eq('record_type', 'spool');
      });

      const results = await Promise.all(updatePromises);
      
      // Check for errors in weight updates
      const updateError = results.find(result => result?.error);
      if (updateError?.error) throw updateError.error;

      // 2. Hard delete the usage log (full row removal)
      const { error: deleteError } = await supabase
        .from('printer_usage_logs')
        .delete()
        .eq('id', logId)
        .eq('record_type', 'usage');

      if (deleteError) throw deleteError;

      // 3. Show success (real-time subscription will update UI)
      toast.success('Usage log deleted and materials restored to inventory');
    } catch (error) {
      console.error('Error deleting usage:', error);
      toast.error(error.message || 'Failed to delete usage log');
      // Reload data to ensure UI is in sync on error
      await Promise.all([loadSpools(), loadUsageLogs()]);
    }
  };

  // Handle add spool usage row in form
  const handleAddSpoolUsageRow = () => {
    setNewUsage({
      ...newUsage,
      spoolUsages: [...newUsage.spoolUsages, { spoolId: '', amountUsed: '' }]
    });
  };

  // Handle remove spool usage row in form
  const handleRemoveSpoolUsageRow = (index) => {
    const updated = newUsage.spoolUsages.filter((_, i) => i !== index);
    setNewUsage({ ...newUsage, spoolUsages: updated });
  };

  // Handle update spool usage row
  const handleUpdateSpoolUsageRow = (index, field, value) => {
    const updated = newUsage.spoolUsages.map((su, i) => 
      i === index ? { ...su, [field]: value } : su
    );
    setNewUsage({ ...newUsage, spoolUsages: updated });
  };

  // Get spool info by id
  const getSpoolInfo = (spoolId) => {
    return spools.find(s => s.id === spoolId);
  };

  // Format date time to 12-hour format
  const formatDateTime = (date) => {
    return format(date, 'MMM dd, yyyy hh:mm a');
  };

  return (
    <div className="h-full w-full flex flex-col">
      <div className="w-full px-4 lg:px-8 py-6 flex-1 flex flex-col overflow-hidden">
        {/* Loading State */}
        {isLoading ? (
          <div className="glass-card p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-gray-400">Loading printer usage data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="flex items-center gap-3 lg:gap-6 mb-6 lg:mb-8 flex-shrink-0">
              <div className="glass-card p-3 lg:p-6 flex-1">
                <p className="text-gray-400 text-[10px] lg:text-sm mb-1 lg:mb-2">Total Spools / Grams</p>
                <p className="text-xl lg:text-3xl font-bold text-white">
                  {totalSpools} / {totalInitialWeight}g
                </p>
              </div>
              <div className="glass-card p-3 lg:p-6 flex-1">
                <p className="text-gray-400 text-[10px] lg:text-sm mb-1 lg:mb-2">Remaining Spools / Grams</p>
                <p className="text-xl lg:text-3xl font-bold text-green-400">
                  {spoolsWithMaterial} / {totalRemainingWeight}g
                </p>
              </div>
              <button
                onClick={() => setShowSpoolInventoryModal(true)}
                className="btn-primary flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-1.5 lg:py-2 text-sm lg:text-base flex-shrink-0"
              >
                <Plus size={16} className="lg:hidden" />
                <Plus size={18} className="hidden lg:block" />
                <span className="lg:hidden">Spool</span>
                <span className="hidden lg:inline">Spool Inventory</span>
              </button>
            </div>

        {/* Usage Logs Table */}
        <div className="glass-card flex-1 flex flex-col overflow-hidden">
          <div className="p-6 border-b border-white/10 flex items-center justify-between flex-shrink-0">
            <h2 className="text-xl font-bold text-white">Usage Logs</h2>
            <button
              onClick={() => setShowAddUsageModal(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={18} />
              Add Usage
            </button>
          </div>
          <div className="overflow-auto flex-1">
            <table className="w-full">
              <thead className="sticky top-0 bg-[#1a1f2e] z-10">
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-gray-400 font-medium">
                    Date / Time{userRole === 'admin' && <span className="text-xs text-blue-400 ml-1">[dbl-click]</span>}
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">Spool ID</th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    Purpose{userRole === 'admin' && <span className="text-xs text-blue-400 ml-1">[dbl-click]</span>}
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    Amount Used{userRole === 'admin' && <span className="text-xs text-blue-400 ml-1">[dbl-click]</span>}
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">By User</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {usageLogs.map((log) => {
                  const isMultiSpool = log.spools.length > 1;
                  // Check ownership using UUID (more secure)
                  const canDelete = currentUser && currentUser.id === log.userId;
                  const isAdmin = userRole === 'admin';
                  
                  return (
                    <React.Fragment key={log.id}>
                      {log.spools.map((spoolUsage, spoolIndex) => {
                        const spoolInfo = getSpoolInfo(spoolUsage.spoolId);
                        const isFirstRow = spoolIndex === 0;
                        const isEditingThisLog = editingUsageLog?.logId === log.id && editingUsageLog?.spoolIndex === spoolIndex;
                        
                        return (
                          <tr 
                            key={`${log.id}-${spoolUsage.spoolId}-${spoolIndex}`}
                            className={`border-b border-white/5 hover:bg-white/5 transition-colors ${
                              isMultiSpool ? 'bg-blue-500/5' : ''
                            }`}
                          >
                            {isFirstRow && (
                              <td 
                                className={`p-4 text-gray-300 ${isAdmin ? 'cursor-pointer hover:bg-white/10' : ''}`}
                                rowSpan={log.spools.length}
                                onDoubleClick={() => isAdmin && handleUsageLogDoubleClick(log, spoolUsage, 'dateTime', spoolIndex)}
                                title={isAdmin ? 'Double-click to edit' : ''}
                              >
                                {isEditingThisLog && editingUsageField === 'dateTime' ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="datetime-local"
                                      value={editingUsageValue}
                                      onChange={(e) => setEditingUsageValue(e.target.value)}
                                      className="px-2 py-1 bg-white/10 border border-blue-500 rounded text-white text-sm"
                                      autoFocus
                                    />
                                    <button
                                      onClick={handleSaveUsageLogEdit}
                                      className="p-1 hover:bg-green-500/20 text-green-400 rounded"
                                      title="Save"
                                    >
                                      <Save size={16} />
                                    </button>
                                    <button
                                      onClick={handleCancelUsageLogEdit}
                                      className="p-1 hover:bg-red-500/20 text-red-400 rounded"
                                      title="Cancel"
                                    >
                                      <X size={16} />
                                    </button>
                                  </div>
                                ) : (
                                  formatDateTime(log.dateTime)
                                )}
                              </td>
                            )}
                            <td className="p-4">
                              <span className="text-white font-medium">
                                #{spoolUsage.spoolId}
                                {spoolInfo && (
                                  <span className="text-gray-500 text-sm ml-2">
                                    ({spoolInfo.color})
                                  </span>
                                )}
                              </span>
                            </td>
                            {isFirstRow && (
                              <td 
                                className={`p-4 text-white ${isAdmin ? 'cursor-pointer hover:bg-white/10' : ''}`}
                                rowSpan={log.spools.length}
                                onDoubleClick={() => isAdmin && handleUsageLogDoubleClick(log, spoolUsage, 'purpose', spoolIndex)}
                                title={isAdmin ? 'Double-click to edit' : ''}
                              >
                                {isEditingThisLog && editingUsageField === 'purpose' ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={editingUsageValue}
                                      onChange={(e) => setEditingUsageValue(e.target.value)}
                                      className="px-2 py-1 bg-white/10 border border-blue-500 rounded text-white text-sm flex-1"
                                      autoFocus
                                    />
                                    <button
                                      onClick={handleSaveUsageLogEdit}
                                      className="p-1 hover:bg-green-500/20 text-green-400 rounded"
                                      title="Save"
                                    >
                                      <Save size={16} />
                                    </button>
                                    <button
                                      onClick={handleCancelUsageLogEdit}
                                      className="p-1 hover:bg-red-500/20 text-red-400 rounded"
                                      title="Cancel"
                                    >
                                      <X size={16} />
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    {log.purpose}
                                    {isMultiSpool && (
                                      <span className="ml-2 text-xs text-blue-400">(Multi-spool)</span>
                                    )}
                                  </>
                                )}
                              </td>
                            )}
                            <td 
                              className={`p-4 text-white ${isAdmin ? 'cursor-pointer hover:bg-white/10' : ''}`}
                              onDoubleClick={() => isAdmin && handleUsageLogDoubleClick(log, spoolUsage, 'amountUsed', spoolIndex)}
                              title={isAdmin ? 'Double-click to edit' : ''}
                            >
                              {isEditingThisLog && editingUsageField === 'amountUsed' ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    value={editingUsageValue}
                                    onChange={(e) => setEditingUsageValue(e.target.value)}
                                    className="px-2 py-1 bg-white/10 border border-blue-500 rounded text-white text-sm w-24"
                                    autoFocus
                                  />
                                  <span className="text-gray-400">g</span>
                                  <button
                                    onClick={handleSaveUsageLogEdit}
                                    className="p-1 hover:bg-green-500/20 text-green-400 rounded"
                                    title="Save"
                                  >
                                    <Save size={16} />
                                  </button>
                                  <button
                                    onClick={handleCancelUsageLogEdit}
                                    className="p-1 hover:bg-red-500/20 text-red-400 rounded"
                                    title="Cancel"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                `${spoolUsage.amountUsed}g`
                              )}
                            </td>
                            {isFirstRow && (
                              <>
                                <td className="p-4 text-gray-300" rowSpan={log.spools.length}>
                                  {log.user}
                                </td>
                                <td className="p-4" rowSpan={log.spools.length}>
                                  <div className="flex items-center justify-end">
                                    {canDelete ? (
                                      <button
                                        onClick={() => handleDeleteUsage(log.id)}
                                        className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                                        title="Delete"
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    ) : (
                                      <span className="text-gray-600 text-sm">No access</span>
                                    )}
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            
            {usageLogs.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-400">No usage logs yet</p>
              </div>
            )}
          </div>
        </div>
          </>
        )}
      </div>

      {/* Spool Inventory Modal */}
      {showSpoolInventoryModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-card max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Spool Inventory</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowAddSpoolModal(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <Plus size={18} />
                  Add Spool
                </button>
                <button
                  onClick={() => setShowSpoolInventoryModal(false)}
                  className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="overflow-auto flex-1">
              <table className="w-full">
                <thead className="sticky top-0 bg-[#0a0e1a]">
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-gray-400 font-medium">Spool ID</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Color</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Material</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Initial Weight (g)</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Remaining Weight (g)</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Used (%)</th>
                    <th className="text-right p-4 text-gray-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {spools.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="p-12 text-center">
                        <p className="text-gray-400 text-lg">No spools yet</p>
                        <p className="text-gray-500 text-sm mt-2">Add your first spool to get started</p>
                      </td>
                    </tr>
                  ) : (
                    spools.map((spool) => {
                      // Calculate used percentage dynamically
                      const usedPercentage = spool.initialWeight > 0 
                        ? ((spool.initialWeight - spool.remainingWeight) / spool.initialWeight * 100).toFixed(1)
                        : '0.0';
                      const isEditingThis = editingSpoolId === spool.id;
                      
                      return (
                        <tr key={spool.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="p-4 text-white font-medium">#{spool.id}</td>
                          
                          {/* Color - Double click to edit */}
                          <td 
                            className="p-4 cursor-pointer"
                            onDoubleClick={() => {
                              setEditingSpoolId(spool.id);
                              setEditingField('color');
                            }}
                            title="Double-click to edit"
                          >
                            {isEditingThis && editingField === 'color' ? (
                              <input
                                type="text"
                                value={spool.color}
                                onChange={(e) => handleEditSpool(spool.id, 'color', e.target.value)}
                                onBlur={handleSaveSpoolEdit}
                                autoFocus
                                className="px-3 py-1 bg-white/5 border border-blue-500 rounded text-white text-sm w-32"
                              />
                            ) : (
                              <span className="text-white">{spool.color}</span>
                            )}
                          </td>
                          
                          {/* Material - Double click to edit */}
                          <td 
                            className="p-4 cursor-pointer"
                            onDoubleClick={() => {
                              setEditingSpoolId(spool.id);
                              setEditingField('material');
                            }}
                            title="Double-click to edit"
                          >
                            {isEditingThis && editingField === 'material' ? (
                              <select
                                value={spool.material}
                                onChange={(e) => {
                                  handleEditSpool(spool.id, 'material', e.target.value);
                                  handleSaveSpoolEdit();
                                }}
                                onBlur={handleSaveSpoolEdit}
                                autoFocus
                                className="px-3 py-1 bg-[#1a1f2e] border border-blue-500 rounded text-white text-sm [&>option]:bg-[#1a1f2e]"
                              >
                                <option value="PLA">PLA</option>
                                <option value="ABS">ABS</option>
                                <option value="PETG">PETG</option>
                                <option value="TPU">TPU</option>
                              </select>
                            ) : (
                              <span className="text-white">{spool.material}</span>
                            )}
                          </td>
                          
                          <td className="p-4 text-white">{spool.initialWeight}g</td>
                          
                          {/* Remaining Weight - Double click to edit */}
                          <td 
                            className="p-4 cursor-pointer"
                            onDoubleClick={() => {
                              setEditingSpoolId(spool.id);
                              setEditingField('remainingWeight');
                            }}
                            title="Double-click to edit"
                          >
                            {isEditingThis && editingField === 'remainingWeight' ? (
                              <input
                                type="number"
                                value={spool.remainingWeight}
                                onChange={(e) => handleEditSpool(spool.id, 'remainingWeight', Number(e.target.value))}
                                onBlur={handleSaveSpoolEdit}
                                autoFocus
                                max={spool.initialWeight}
                                className="px-3 py-1 bg-white/5 border border-blue-500 rounded text-white text-sm w-24"
                              />
                            ) : (
                              <span className={`font-medium ${
                                spool.remainingWeight === 0 ? 'text-red-400' : 
                                spool.remainingWeight < spool.initialWeight * 0.2 ? 'text-yellow-400' : 
                                'text-green-400'
                              }`}>
                                {spool.remainingWeight}g
                              </span>
                            )}
                          </td>
                          
                          {/* Used % - Auto-updates */}
                          <td className="p-4 text-gray-400">{usedPercentage}%</td>
                          
                          <td className="p-4">
                            <div className="flex items-center justify-end gap-2">
                              {isEditingThis ? (
                                <>
                                  <button
                                    onClick={handleSaveSpoolEdit}
                                    className="p-2 rounded-lg hover:bg-green-500/20 text-green-400 transition-colors"
                                    title="Save"
                                  >
                                    <Save size={16} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingSpoolId(null);
                                      setEditingField(null);
                                      loadSpools(); // Reload to reset any unsaved changes
                                    }}
                                    className="p-2 rounded-lg hover:bg-gray-500/20 text-gray-400 transition-colors"
                                    title="Cancel"
                                  >
                                    <X size={16} />
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => handleDeleteSpool(spool.id)}
                                  className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add Spool Modal */}
      {showAddSpoolModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-card p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold text-white mb-6">Add New Spool</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Color</label>
                <input
                  type="text"
                  value={newSpool.color}
                  onChange={(e) => setNewSpool({ ...newSpool, color: e.target.value })}
                  placeholder="e.g., White, Black, Red"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
                />
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-2">Material</label>
                <select
                  value={newSpool.material}
                  onChange={(e) => setNewSpool({ ...newSpool, material: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-blue-500/50"
                >
                  <option value="PLA">PLA</option>
                  <option value="ABS">ABS</option>
                  <option value="PETG">PETG</option>
                  <option value="TPU">TPU</option>
                </select>
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-2">Initial Weight (g)</label>
                <input
                  type="number"
                  value={newSpool.initialWeight}
                  onChange={(e) => setNewSpool({ ...newSpool, initialWeight: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-blue-500/50"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddSpoolModal(false);
                  setNewSpool({ initialWeight: 1000, color: '', material: 'PLA' });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSpool}
                className="btn-primary"
              >
                Add Spool
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Usage Modal */}
      {showAddUsageModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="glass-card p-8 max-w-2xl w-full my-8">
            <h2 className="text-2xl font-bold text-white mb-6">Add Usage Log</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Purpose</label>
                <input
                  type="text"
                  value={newUsage.purpose}
                  onChange={(e) => setNewUsage({ ...newUsage, purpose: e.target.value })}
                  placeholder="e.g., Prototype Housing, Test Fixture"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-gray-400 text-sm">Spool Usage</label>
                  <button
                    onClick={handleAddSpoolUsageRow}
                    className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                  >
                    <Plus size={16} />
                    Add Spool
                  </button>
                </div>
                
                <div className="space-y-3">
                  {newUsage.spoolUsages.map((su, index) => {
                    // Get selected spool to check available weight
                    const selectedSpool = spools.find(s => s.id === Number(su.spoolId));
                    const amountUsed = Number(su.amountUsed) || 0;
                    const isInsufficient = selectedSpool && amountUsed > selectedSpool.remainingWeight;
                    
                    return (
                    <div key={index} className="flex gap-3">
                      <div className="flex-1">
                        <select
                          value={su.spoolId}
                          onChange={(e) => handleUpdateSpoolUsageRow(index, 'spoolId', e.target.value)}
                          className="w-full px-4 py-3 bg-[#1a1f2e] border border-white/10 rounded-lg text-white focus:outline-none focus:border-blue-500/50 [&>option]:bg-[#1a1f2e] [&>option]:text-white"
                        >
                          <option value="" className="bg-[#1a1f2e] text-gray-400">Select Spool</option>
                          {spools.filter(s => s.remainingWeight > 0).map(spool => (
                            <option key={spool.id} value={spool.id} className="bg-[#1a1f2e] text-white">
                              #{spool.id} - {spool.color} ({spool.material}) - {spool.remainingWeight}g left
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="w-32">
                        <input
                          type="number"
                          value={su.amountUsed}
                          onChange={(e) => handleUpdateSpoolUsageRow(index, 'amountUsed', e.target.value)}
                          placeholder="Grams"
                          className={`w-full px-4 py-3 bg-white/5 border rounded-lg text-white placeholder-gray-500 focus:outline-none ${
                            isInsufficient 
                              ? 'border-red-500 focus:border-red-500' 
                              : 'border-white/10 focus:border-blue-500/50'
                          }`}
                        />
                        {isInsufficient && (
                          <p className="text-red-400 text-xs mt-1">Insufficient material</p>
                        )}
                      </div>
                      {newUsage.spoolUsages.length > 1 && (
                        <button
                          onClick={() => handleRemoveSpoolUsageRow(index)}
                          className="p-3 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddUsageModal(false);
                  setNewUsage({
                    purpose: '',
                    spoolUsages: [{ spoolId: '', amountUsed: '' }]
                  });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleAddUsage}
                className="btn-primary"
              >
                Add Usage
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-card p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold text-white mb-4">Confirm Delete</h2>
            
            {deleteConfirmation.type === 'usage' ? (
              <div className="space-y-4">
                <p className="text-gray-300">
                  Are you sure you want to delete this usage log?
                </p>
                <div className="bg-white/5 rounded-lg p-4 space-y-2">
                  <p className="text-gray-400 text-sm">
                    <span className="font-medium text-white">Purpose:</span> {deleteConfirmation.data?.purpose}
                  </p>
                  <p className="text-gray-400 text-sm">
                    <span className="font-medium text-white">Materials used:</span>
                  </p>
                  <ul className="ml-4 space-y-1">
                    {deleteConfirmation.data?.spools.map((spool, idx) => (
                      <li key={idx} className="text-gray-400 text-sm">
                        • Spool #{spool.spoolId}: {spool.amountUsed}g
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-yellow-400 text-sm">
                  ⚠️ Materials will be restored to spool inventory
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-300">
                  Are you sure you want to delete this spool?
                </p>
                <div className="bg-white/5 rounded-lg p-4 space-y-2">
                  <p className="text-gray-400 text-sm">
                    <span className="font-medium text-white">Spool ID:</span> #{deleteConfirmation.data?.id}
                  </p>
                  <p className="text-gray-400 text-sm">
                    <span className="font-medium text-white">Color:</span> {deleteConfirmation.data?.color}
                  </p>
                  <p className="text-gray-400 text-sm">
                    <span className="font-medium text-white">Material:</span> {deleteConfirmation.data?.material}
                  </p>
                  <p className="text-gray-400 text-sm">
                    <span className="font-medium text-white">Remaining:</span> {deleteConfirmation.data?.remainingWeight}g
                  </p>
                </div>
                <p className="text-red-400 text-sm">
                  ⚠️ This action cannot be undone
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setDeleteConfirmation({ show: false, type: null, id: null, data: null })}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={deleteConfirmation.type === 'usage' ? confirmDeleteUsage : confirmDeleteSpool}
                className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrinterUsage;
